import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { DialogFooter } from "@/components/ui/dialog";
import { CreatableSelect } from "@/components/creatable-select/CreatableSelect";
import { ConversionPreview } from "@/components/conversion-preview/ConversionPreview";
import { useCreateCategory } from "@metadataHooks";
import {
  useFetchSubCategories,
  useCreateSubCategory,
} from "@metadataHooks";
import { useExchangeRates } from "@/hooks/useExchangeRates";
import { toast } from "sonner";
import { format } from "date-fns";
import { useCurrencyList } from "@/hooks/useCurrencyList";
import { formatCurrency } from "@/lib/currency.utils";

export interface TransactionFormProps {
  transaction?: {
    id: string;
    type: string;
    amount: number;
    account_id: string;
    category_id: string | null;
    sub_category_id?: string | null;
    currency: string;
    fee?: number;
    description: string | null;
    notes: string | null;
    transaction_date: string;
  };
  onSave: (data: TransactionFormData) => void | Promise<void>;
  onCancel: () => void;
  accounts: Array<{
    id: string;
    name: string;
    nickname?: string;
    currency?: string;
    balance?: number;
    type?: string;
  }>;
  categories: Array<{
    id: string;
    name: string;
    type: string;
    icon: string | null;
  }>;
  defaultAccountId?: string;
  defaultCurrency?: string;
  isLoadingAccounts?: boolean;
  onTransferSave?: (data: TransferFormData) => void | Promise<void>;
}

export interface TransactionFormData {
  type: "income" | "expense";
  amount: number;
  account_id: string;
  category_id: string | null;
  sub_category_id: string | null;
  currency: string;
  fee: number;
  description: string | null;
  notes: string | null;
  transaction_date: string;
}

export interface TransferFormData {
  sourceAccountId: string;
  targetAccountId: string;
  amount: number;
  fee: number;
  currency: string;
  description: string | null;
  notes: string | null;
  transactionDate: string;
}

export function TransactionForm({
  transaction,
  onSave,
  onCancel,
  accounts,
  categories,
  defaultAccountId,
  defaultCurrency,
  isLoadingAccounts = false,
  onTransferSave,
}: TransactionFormProps) {
  const createCategory = useCreateCategory();
  const createSubCategory = useCreateSubCategory();
  const { getRate, isLoading: isLoadingRates } = useExchangeRates();
  const {
    data: availableCurrencies,
    isLoading: isLoadingCurrencies,
    error: errorLoadingCurrencies,
  } = useCurrencyList();

  const [type, setType] = useState<"income" | "expense" | "transfer">(
    (transaction?.type as "income" | "expense" | "transfer") ?? "expense",
  );
  const [amount, setAmount] = useState(transaction?.amount?.toString() ?? "");
  const [accountId, setAccountId] = useState(
    transaction?.account_id ?? defaultAccountId ?? "",
  );
  const [sourceAccountId, setSourceAccountId] = useState("");
  const [targetAccountId, setTargetAccountId] = useState("");
  const [categoryId, setCategoryId] = useState(transaction?.category_id ?? "");
  const [subCategoryId, setSubCategoryId] = useState(
    transaction?.sub_category_id ?? "",
  );
  const [currency, setCurrency] = useState(
    transaction?.currency ?? defaultCurrency ?? "USD",
  );
  const [fee, setFee] = useState(
    transaction?.fee !== undefined ? transaction.fee.toString() : "0",
  );
  const [description, setDescription] = useState(
    transaction?.description ?? "",
  );
  const [notes, setNotes] = useState(transaction?.notes ?? "");
  const [transactionDate, setTransactionDate] = useState(
    transaction?.transaction_date ?? format(new Date(), "yyyy-MM-dd"),
  );
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);

  // Derived account data for balance display and conversion preview
  const selectedAccount = accounts.find((a) => a.id === accountId);
  const selectedSourceAccount = accounts.find((a) => a.id === sourceAccountId);
  const selectedTargetAccount = accounts.find((a) => a.id === targetAccountId);

  const parsedAmount = parseFloat(amount) || 0;

  // Fetch subcategories for the selected category
  const { data: subCategories = [] } = useFetchSubCategories(
    categoryId || undefined,
  );

  // Filter categories by transaction type
  const filteredCategories = categories.filter((c) => c.type === type);

  // Reset subCategoryId when categoryId changes (6.3)
  useEffect(() => {
    setSubCategoryId("");
  }, [categoryId]);

  // Reset both categoryId and subCategoryId when type changes (6.4)
  // Also clear category if it doesn't match the new type
  useEffect(() => {
    if (categoryId) {
      const selectedCategory = categories.find((c) => c.id === categoryId);
      if (selectedCategory && selectedCategory.type !== type) {
        setCategoryId("");
        setSubCategoryId("");
      }
    }
    if (type === "transfer") {
      setCategoryId("");
      setSubCategoryId("");
    }
  }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateCategory = async (name: string): Promise<string> => {
    setIsCreatingCategory(true);
    try {
      const newCategory = await createCategory.mutateAsync({
        name,
        type,
        icon: "📦",
      });
      toast.success("Category created");
      return newCategory.id;
    } catch (error) {
      toast.error(error.message || "Failed to create category");
      throw error;
    } finally {
      setIsCreatingCategory(false);
    }
  };

  const handleCreateSubCategory = async (name: string): Promise<string> => {
    try {
      const newSubCategory = await createSubCategory.mutateAsync({
        categoryId: categoryId,
        name,
      });
      toast.success("Subcategory created");
      return newSubCategory.id;
    } catch (error) {
      toast.error(error.message || "Failed to create subcategory");
      throw error;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }

    const feeValue = parseFloat(fee) || 0;
    if (feeValue < 0) {
      toast.error("Fee cannot be negative");
      return;
    }

    // Validate fee decimal precision (max 2 decimal places)
    const feeStr = feeValue.toString();
    const decimalIndex = feeStr.indexOf(".");
    if (decimalIndex !== -1 && feeStr.length - decimalIndex - 1 > 2) {
      toast.error("Fee can have at most 2 decimal places");
      return;
    }

    // Handle transfer type
    if (type === "transfer") {
      if (!sourceAccountId) {
        toast.error("Please select a source account");
        return;
      }
      if (!targetAccountId) {
        toast.error("Please select a target account");
        return;
      }
      if (sourceAccountId === targetAccountId) {
        toast.error("Source and target accounts must be different");
        return;
      }
      if (!currency) {
        toast.error("Please select a currency");
        return;
      }

      if (onTransferSave) {
        onTransferSave({
          sourceAccountId,
          targetAccountId,
          amount: parseFloat(amount),
          fee: feeValue,
          currency,
          description: description || null,
          notes: notes || null,
          transactionDate,
        });
      }
      return;
    }

    // Handle income/expense types
    if (!accountId) {
      toast.error("Please select an account");
      return;
    }
    if (!currency) {
      toast.error("Please select a currency");
      return;
    }

    onSave({
      type: type as "income" | "expense",
      amount: parseFloat(amount),
      account_id: accountId,
      category_id: categoryId || null,
      sub_category_id: subCategoryId || null,
      currency,
      fee: feeValue,
      description: description || null,
      notes: notes || null,
      transaction_date: transactionDate,
    });
  };

  const categoryOptions = filteredCategories.map((c) => ({
    value: c.id,
    label: c.name,
    icon: c.icon || undefined,
  }));

  const subCategoryOptions = subCategories.map((sc) => ({
    value: sc.id,
    label: sc.name,
    icon: sc.icon || undefined,
  }));

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Type</Label>
        <Select
          value={type}
          onValueChange={(v) => {
            setType(v as "income" | "expense" | "transfer");
          }}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="income">Income</SelectItem>
            <SelectItem value="expense">Expense</SelectItem>
            <SelectItem value="transfer">Inter-Account Transfer</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="amount">Amount</Label>
          <Input
            id="amount"
            type="number"
            step="0.01"
            min="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="date">Date</Label>
          <Input
            id="date"
            type="date"
            value={transactionDate}
            onChange={(e) => setTransactionDate(e.target.value)}
            required
          />
        </div>
      </div>

      {type === "transfer" ? (
        <>
          <div className="space-y-2">
            <Label>Source Account</Label>
            <Select value={sourceAccountId} onValueChange={setSourceAccountId}>
              <SelectTrigger>
                <SelectValue placeholder="Select source account" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.nickname || a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isLoadingAccounts && sourceAccountId ? (
              <Skeleton className="h-4 w-32" />
            ) : selectedSourceAccount?.currency ? (
              <p className="text-sm text-muted-foreground">
                {formatCurrency(
                  selectedSourceAccount.balance,
                  selectedSourceAccount.currency,
                )}
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label>Target Account</Label>
            <Select value={targetAccountId} onValueChange={setTargetAccountId}>
              <SelectTrigger>
                <SelectValue placeholder="Select target account" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.nickname || a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isLoadingAccounts && targetAccountId ? (
              <Skeleton className="h-4 w-32" />
            ) : selectedTargetAccount?.currency ? (
              <p className="text-sm text-muted-foreground">
                {formatCurrency(
                  selectedTargetAccount.balance,
                  selectedTargetAccount.currency,
                )}
              </p>
            ) : null}
          </div>
        </>
      ) : (
        <div className="space-y-2">
          <Label>Account</Label>
          <Select value={accountId} onValueChange={setAccountId}>
            <SelectTrigger>
              <SelectValue placeholder="Select account" />
            </SelectTrigger>
            <SelectContent>
              {accounts.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.nickname || a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isLoadingAccounts && accountId ? (
            <Skeleton className="h-4 w-32" />
          ) : selectedAccount?.currency ? (
            <p className="text-sm text-muted-foreground">
              {formatCurrency(selectedAccount.balance, selectedAccount.currency)}
            </p>
          ) : null}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="currency">Currency</Label>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger id="currency" aria-label="Currency">
              <SelectValue placeholder="Select currency" />
            </SelectTrigger>
            {isLoadingCurrencies && "Loading Currencies"}
            {!isLoadingCurrencies && !errorLoadingCurrencies ? (
              <SelectContent>
                {availableCurrencies.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            ) : (
              "Failed to load currencies"
            )}
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="fee">Fee (optional)</Label>
          <Input
            id="fee"
            type="number"
            step="0.01"
            min="0"
            value={fee}
            onChange={(e) => setFee(e.target.value)}
            placeholder="0.00"
          />
        </div>
      </div>

      {type !== "transfer" && selectedAccount?.currency && (
        <ConversionPreview
          amount={parsedAmount}
          fromCurrency={currency}
          toCurrency={selectedAccount.currency}
          getRate={getRate}
          isLoadingRates={isLoadingRates}
        />
      )}

      {type === "transfer" && (
        <>
          {selectedSourceAccount?.currency && (
            <ConversionPreview
              amount={parsedAmount}
              fromCurrency={currency}
              toCurrency={selectedSourceAccount.currency}
              label="Source account"
              getRate={getRate}
              isLoadingRates={isLoadingRates}
            />
          )}
          {selectedTargetAccount?.currency && (
            <ConversionPreview
              amount={parsedAmount}
              fromCurrency={currency}
              toCurrency={selectedTargetAccount.currency}
              label="Target account"
              getRate={getRate}
              isLoadingRates={isLoadingRates}
            />
          )}
        </>
      )}

      {type !== "transfer" && (
        <div className="space-y-2">
          <Label>Category</Label>
          <CreatableSelect
            value={categoryId}
            onValueChange={setCategoryId}
            options={categoryOptions}
            placeholder="Select or create category"
            onCreate={handleCreateCategory}
            disabled={isCreatingCategory}
          />
        </div>
      )}

      {type !== "transfer" && (
        <div className="space-y-2">
          <Label>Subcategory</Label>
          <CreatableSelect
            value={subCategoryId}
            onValueChange={setSubCategoryId}
            options={subCategoryOptions}
            placeholder="Select or create subcategory"
            onCreate={handleCreateSubCategory}
            disabled={!categoryId || createSubCategory.isPending}
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Input
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What was this for?"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Additional notes..."
          rows={2}
        />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isCreatingCategory}>
          {transaction ? "Update" : "Add"}
        </Button>
      </DialogFooter>
    </form>
  );
}
